import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/Reveal";

export default function Reviews({ reviews }) {
  const [ref, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [index, setIndex] = useState(0);
  const onSelect = useCallback(() => { if (embla) setIndex(embla.selectedScrollSnap()); }, [embla]);
  useEffect(() => {
    if (!embla) return undefined;
    embla.on("select", onSelect);
    return () => { embla.off("select", onSelect); };
  }, [embla, onSelect]);

  return (
    <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-32" data-testid="reviews-section">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <Reveal>
          <SectionLabel index="06">What customers say</SectionLabel>
          <h2 className="mt-6 font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">In their words.</h2>
        </Reveal>
        {reviews.length > 1 && <div className="flex gap-3">
          <button onClick={() => embla?.scrollPrev()} aria-label="Previous review" data-testid="reviews-prev" className="grid h-12 w-12 place-items-center rounded-full border border-charcoal/40 transition-colors hover:bg-forest hover:text-paper"><ArrowLeft className="h-5 w-5" strokeWidth={1.5} /></button>
          <button onClick={() => embla?.scrollNext()} aria-label="Next review" data-testid="reviews-next" className="grid h-12 w-12 place-items-center rounded-full border border-charcoal/40 transition-colors hover:bg-forest hover:text-paper"><ArrowRight className="h-5 w-5" strokeWidth={1.5} /></button>
        </div>}
      </div>      <div className="mt-14 overflow-hidden" ref={ref}>
        <div className="flex">
          {reviews.map((r, i) => (
            <article key={i} className="min-w-0 shrink-0 grow-0 basis-full pr-5 md:basis-1/2 lg:basis-1/3" data-testid={`review-${i}`}>
              <div className="flex h-full flex-col border border-stone bg-[#FBF9F4] p-8">
                <div className="flex items-center justify-between">
                  <Quote className="h-7 w-7 text-forest" strokeWidth={1} />
                  {r.source && <span className="bg-stone px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.16em] text-charcoal">{r.source}</span>}
                </div>
                <p className="mt-6 flex-1 font-serif text-2xl leading-snug text-charcoal">{r.text}</p>
                <p className="mt-8 text-sm text-charcoal/75">{[r.name, r.place].filter(Boolean).join(", ")}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
      {reviews.length > 1 && <div className="mt-8 flex gap-2" aria-hidden="true">
        {reviews.map((_, i) => <span key={i} className={`h-px transition-all duration-500 ${i === index ? "w-12 bg-forest" : "w-6 bg-stone-dark"}`} />)}
      </div>}
    </section>
  );
}
